import {chain, reject, sortBy} from 'lodash';
import {createSelector} from 'reselect';

import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {LegacySidePanelEntryPoint} from '../../helpers/models/legacyApplicationHelpers';
import {FrameTypesEnum} from '../../models/api/apiTypes';
import {ApplicationEntryPointModel} from '../../models/api/applicationEntryPointModel';
import {contactsIntegrationFrameId, IntegrationModel} from '../../models/api/integrationModel';
import {PluginModel} from '../../models/api/pluginModel';
import {getInboxSidePanelEntryPoints} from '../data/applications/applicationsSelectors';
import {getLegacySidePanelEntryPoints} from '../data/applications/legacyApplicationsSelectors';
import {getPluginIntegrations} from '../data/dataSelectors';
import {getFlatPluginsForUser} from '../data/plugins/pluginsSelectors';
import {getHiddenFrameIds, getSortedFrameIds} from '../data/preferences/preferencesSelectors';
import {
  getHasNewApplicationsEntryPoints,
  getHasNewIntegrationsDiscoverabilityP4,
} from '../featureFlags/featureFlagsSelectors';

/*
 * Constants.
 */

const CUSTOM_PLUGIN_TYPE_ID_PREFIX = 'custom-';

/*
 * Frame types.
 */

export type SidePanelFrame =
  | LegacySidePanelEntryPoint
  | PluginModel
  | IntegrationModel
  | typeof managerFrame
  | typeof calendarFrame
  | typeof startGuideFrame
  | typeof keyboardHelpFrame
  | typeof customerViewFrame
  | ApplicationEntryPointModel;

/*
 * Frames.
 */

const startGuideFrame = {
  frameType: FrameTypesEnum.START_GUIDE as const,
  frameId: FrameTypesEnum.START_GUIDE,
  name: '',
};

const calendarFrame = {
  frameType: FrameTypesEnum.CALENDAR as const,
  frameId: FrameTypesEnum.CALENDAR,
  name: '',
};

const managerFrame = {
  frameType: FrameTypesEnum.MANAGER as const,
  frameId: FrameTypesEnum.MANAGER,
  name: '',
};

export const keyboardHelpFrame = {
  frameType: FrameTypesEnum.KEYBOARD_HELP as const,
  frameId: FrameTypesEnum.KEYBOARD_HELP,
  name: '',
};

export const customerViewFrame = {
  frameType: FrameTypesEnum.CUSTOMER_VIEW as const,
  frameId: FrameTypesEnum.CUSTOMER_VIEW,
  color: PaletteColorsEnum.TEAL,
  name: '',
};

/*
 * Selectors.
 */

export const getSidePanelFrames = createSelector(
  [
    getHasNewApplicationsEntryPoints,
    getFlatPluginsForUser,
    getPluginIntegrations,
    getLegacySidePanelEntryPoints,
    getInboxSidePanelEntryPoints,
  ],
  (
    hasNewApplicationsEntryPoints,
    plugins,
    integrations,
    legacySidePanelEntryPoints,
    inboxSidePanelEntryPoints,
  ) => {
    // If we're on the FF we want to omit plugins(these use the legacy sdk)
    // as well as legacy entry points which are custom plugins.
    if (hasNewApplicationsEntryPoints) {
      return chain<SidePanelFrame>(inboxSidePanelEntryPoints)
        .concat(integrations)
        .concat(
          legacySidePanelEntryPoints.filter(
            (ep) => !ep.applicationTypeId.startsWith(CUSTOM_PLUGIN_TYPE_ID_PREFIX),
          ),
        )
        .concat(keyboardHelpFrame)
        .concat(calendarFrame)
        .concat(startGuideFrame)
        .map((f) => {
          // Swap the old contact integration with the customer view if applicable.
          if (f.frameId === contactsIntegrationFrameId) {
            return customerViewFrame;
          }

          return f;
        })
        .sortBy((f) => f.frameId)
        .value();
    }

    return chain<SidePanelFrame>(plugins)
      .concat(integrations)
      .concat(legacySidePanelEntryPoints)
      .concat(keyboardHelpFrame)
      .concat(calendarFrame)
      .concat(startGuideFrame)
      .map((f) => {
        // Swap the old contact integration with the customer view if applicable.
        if (f.frameId === contactsIntegrationFrameId) {
          return customerViewFrame;
        }

        return f;
      })
      .sortBy((f) => f.frameId)
      .value();
  },
);

export const getSidePanelFramesWithManager = createSelector(getSidePanelFrames, (frames) => [
  ...frames,
  managerFrame,
]);

export const getSortedSidePanelFrames = createSelector(getSidePanelFrames, (frames) =>
  sortBy(frames, [(f) => findFrameScore([], f), (f) => f.name]),
);

export const getSortedVisiblePanelFrames = createSelector(
  [
    getSidePanelFramesWithManager,
    getHiddenFrameIds,
    getSortedFrameIds,
    getHasNewIntegrationsDiscoverabilityP4,
  ],
  (frames, hiddenFrameIds, sortedFrameIds, hasNewIntegrationsDiscoverabilityP4) => {
    // TODO: Once the migration is over, use only sortedFrameIds to determine the visible frames.
    const filteredFrames = reject(frames, (f) => {
      if (hasNewIntegrationsDiscoverabilityP4) {
        if (f.frameId === FrameTypesEnum.MANAGER) {
          return false;
        }
        return !sortedFrameIds.includes(f.frameId);
      }
      return hiddenFrameIds.includes(f.frameId);
    });
    return sortBy(filteredFrames, [(f) => findFrameScore(sortedFrameIds, f), (f) => f.name]);
  },
);

function findFrameScore(sortedFrameIds: ReadonlyArray<string>, item: SidePanelFrame) {
  // 5. Sorted frames.
  if (sortedFrameIds.includes(item.frameId)) {
    return 5 + sortedFrameIds.indexOf(item.frameId) / sortedFrameIds.length;
  }

  // 1. Start guide.
  if (item.frameType === FrameTypesEnum.START_GUIDE) {
    return 1;
  }

  // 2. New calendar beta.
  if (item.frameType === FrameTypesEnum.CALENDAR) {
    return 2;
  }

  // 4. Contacts or Customer View.
  if (item.frameId === contactsIntegrationFrameId || item.frameId === customerViewFrame.frameId) {
    return 4;
  }

  // 9. Keyboard shortcuts.
  if (item.frameType === FrameTypesEnum.KEYBOARD_HELP) {
    return 9;
  }

  // 6. Plugins.
  if (item.frameType === FrameTypesEnum.PLUGIN) {
    return 6;
  }

  // 7. Integrations.
  if (item.frameType === FrameTypesEnum.INTEGRATION) {
    return 7;
  }

  // 8. Entry points.
  if (
    item.frameType === FrameTypesEnum.ENTRY_POINT ||
    item.frameType === FrameTypesEnum.APPLICATION_ENTRY_POINT
  ) {
    return 8;
  }

  // 10. Manager.
  if (item.frameType === FrameTypesEnum.MANAGER) {
    return 10;
  }

  throw new Error('This should not happen.');
}
